import {
  FIRST_CLASS_WELCOME_HOURS,
  FIRST_CLASS_WELCOME_MAX_TEXT,
  FIRST_CLASS_WELCOME_MIN_TEXT,
  FIRST_CLASS_WELCOME_TEXT,
  firstClassWelcomeOf,
  type FirstClassWelcome,
} from "@teamna/shared";
import { useState } from "react";
import { Button, FormRow, FormSection, InlineError, SelectField, TextAreaField, ToggleRow, showToast } from "../components/index.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { SaveBar } from "./setup/frame.tsx";

/**
 * Settings, Messages (C3).
 *
 * The welcome before the first class: on or off, how many hours before the class, and the text. A
 * worker job sends it once for each participant (`jobs/first-class-welcome.ts`), by push and by
 * WhatsApp. The start value follows who books: on where clients book for themselves, off for a
 * family business. The settings are saved as one object (`settings.firstClassWelcome`), because
 * the server replaces each settings key as a whole.
 */

function hoursText(hours: number): string {
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

export function FirstClassWelcomeSection({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  if (!business.data) return null;
  const saved = firstClassWelcomeOf(business.data.settings.firstClassWelcome, business.data.bookingFor);
  return (
    <FirstClassWelcomeForm
      // The key resets the form when the stored welcome changes (after a save, or a change elsewhere).
      key={JSON.stringify(saved)}
      saved={saved}
      participant={label("participant")}
      saving={update.isPending}
      error={update.error}
      onSave={(welcome) =>
        update.mutate(
          { settings: { firstClassWelcome: welcome } },
          { onSuccess: () => showToast(welcome.on ? "Welcome saved. It is on." : "Welcome saved. It is off.") },
        )
      }
    />
  );
}

export type FirstClassWelcomeFormProps = {
  /** The stored welcome, start values filled (`firstClassWelcomeOf`). */
  saved: FirstClassWelcome;
  /** The business's word for one participant: "swimmer", "client". */
  participant: string;
  saving?: boolean;
  error?: unknown;
  onSave: (welcome: FirstClassWelcome) => void;
};

/** The form itself, with no request: the section above saves it, a story shows it. */
export function FirstClassWelcomeForm({ saved, participant, saving = false, error = null, onSave }: FirstClassWelcomeFormProps) {
  const [draft, setDraft] = useState<FirstClassWelcome>(saved);
  const text = draft.text.trim();
  const tooShort = text.length < FIRST_CLASS_WELCOME_MIN_TEXT;
  const changed = draft.on !== saved.on || draft.hoursBefore !== saved.hoursBefore || text !== saved.text.trim();
  const hours = FIRST_CLASS_WELCOME_HOURS.includes(draft.hoursBefore as (typeof FIRST_CLASS_WELCOME_HOURS)[number])
    ? FIRST_CLASS_WELCOME_HOURS
    : [...FIRST_CLASS_WELCOME_HOURS, draft.hoursBefore].sort((a, b) => a - b);

  function save() {
    if (!changed || tooShort) return;
    onSave({ on: draft.on, hoursBefore: draft.hoursBefore, text });
  }

  return (
    <div className="flex flex-col gap-4">
      <FormSection
        title="Welcome before the first class"
        description={`Teamna sends this message before the first class of each new ${participant}, one time only. It goes as a push, and as a WhatsApp message where WhatsApp is on and they said yes to it.`}
      >
        <ToggleRow
          label="Send a welcome before the first class"
          hint={draft.on ? "On: each new person gets the welcome one time." : "Off: Teamna sends no welcome."}
          checked={draft.on}
          onChange={(on) => setDraft((current) => ({ ...current, on }))}
        />
        <FormRow label="When">
          <SelectField
            label="Hours before the class"
            value={String(draft.hoursBefore)}
            disabled={!draft.on}
            onChange={(event) => setDraft((current) => ({ ...current, hoursBefore: Number(event.target.value) }))}
          >
            {hours.map((value) => (
              <option key={value} value={value}>
                {hoursText(value)} before the class
              </option>
            ))}
          </SelectField>
        </FormRow>
        <FormRow label="Message" hint="Tell them what to wear, what to bring and when to come.">
          <TextAreaField
            label="Welcome text"
            rows={4}
            value={draft.text}
            maxLength={FIRST_CLASS_WELCOME_MAX_TEXT}
            disabled={!draft.on}
            error={tooShort ? `Write ${FIRST_CLASS_WELCOME_MIN_TEXT} characters or more.` : undefined}
            hint={`${text.length} of ${FIRST_CLASS_WELCOME_MAX_TEXT} characters.`}
            onChange={(event) => setDraft((current) => ({ ...current, text: event.target.value }))}
          />
          {draft.text.trim() !== FIRST_CLASS_WELCOME_TEXT ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              disabled={!draft.on}
              onClick={() => setDraft((current) => ({ ...current, text: FIRST_CLASS_WELCOME_TEXT }))}
            >
              Use the Teamna text
            </Button>
          ) : null}
        </FormRow>
      </FormSection>
      <SaveBar note={changed ? undefined : "No changes yet."}>
        <Button disabled={!changed || tooShort} loading={saving} onClick={save}>
          Save welcome
        </Button>
        {changed ? (
          <Button variant="secondary" onClick={() => setDraft(saved)}>
            Undo changes
          </Button>
        ) : null}
      </SaveBar>
      {error ? <InlineError error={error} /> : null}
    </div>
  );
}
